import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';

// Nur Public-Frontend ist mehrsprachig. Admin bleibt DE-only.
// Übersetzt werden ausschließlich UI-Strings — Event-Felder (name, subheading,
// location) und Email-Adressen bleiben so, wie der Admin sie eingegeben hat.

export type Lang = 'de' | 'en';

const strings = {
  de: {
    'loading': 'Lade…',
    'not_found_title': 'Event nicht gefunden.',
    'not_found_body': 'Bitte prüfe den Link aus deiner Einladung.',
    'error_title': 'Etwas ist schiefgelaufen.',
    'error_body': 'Bitte lade die Seite neu.',
    'page_404_title': 'Kein Event hier.',
    'page_404_body': 'Diese Seite ist nur über einen direkten Event-Link erreichbar.',

    'eyebrow_register': 'Anmeldung',
    'eyebrow_closed': 'Anmeldung geschlossen',
    'eyebrow_done': 'Angemeldet',

    'closed_deadline_text': 'Die Anmeldung für dieses Event ist beendet.',
    'closed_deadline_when': 'Schluss war: {when}',
    'closed_manual': 'Die Anmeldung ist aktuell geschlossen.',

    'field_first_name': 'Vorname',
    'field_last_name': 'Nachname',
    'field_email': 'E-Mail-Adresse',
    'field_extra_guests': 'Weitere Gäste',

    'rsvp_question_solo': 'Ich komme…',
    'rsvp_question_group': 'Wir kommen…',
    'rsvp_confirmed': 'Auf jeden Fall',
    'rsvp_tentative': 'Vielleicht',

    'summary_alone': 'Du meldest dich allein an.',
    'summary_with_one': 'Du meldest dich + 1 weitere Person an (insgesamt 2 Personen).',
    'summary_with_many': 'Du meldest dich + {n} weitere Personen an (insgesamt {total} Personen).',

    'submit_idle': 'Anmelden',
    'submit_busy': 'Senden…',

    'done_title': 'Danke, {firstName}!',
    'done_body_one': 'Wir haben dich für {event} notiert. Wir freuen uns auf dich am {date} um {time} Uhr.',
    'done_body_many': 'Wir haben {persons} Personen für {event} notiert. Wir freuen uns auf euch am {date} um {time} Uhr.',
    'done_tentative_note': 'Du hast „Vielleicht" gewählt — sag gern Bescheid, sobald du sicher Bescheid weißt.',

    'err_email_taken': 'Diese E-Mail-Adresse ist für dieses Event bereits angemeldet.',
    'err_invalid_email': 'Bitte eine gültige E-Mail-Adresse eintragen.',
    'err_invalid_first_name': 'Bitte einen Vornamen eintragen.',
    'err_invalid_last_name': 'Bitte einen Nachnamen eintragen.',
    'err_invalid_extras': 'Anzahl Begleitungen muss zwischen 0 und 10 liegen.',
    'err_invalid_rsvp': 'Bitte „Auf jeden Fall" oder „Vielleicht" auswählen.',
    'err_closed': 'Die Anmeldung ist mittlerweile geschlossen.',
    'err_not_found': 'Event nicht gefunden.',
    'err_generic': 'Anmeldung fehlgeschlagen. Bitte erneut versuchen.',
    'err_guests_not_allowed': 'Für dieses Event sind keine Begleitungen möglich.',

    'lang_toggle_aria': 'Sprache wechseln',

    'calendar_add_title': 'Zum Kalender hinzufügen',
    'calendar_google': 'Google',
    'calendar_apple': 'Apple',
    'calendar_outlook': 'Outlook',

    'location_open_maps_aria': 'Adresse in Google Maps öffnen',
    'more_info_link': 'Mehr erfahren',

    'privacy_link': 'Datenschutzhinweis',
    'privacy_title': 'Datenschutzhinweis',
    'privacy_body': 'Du hast diesen Link bekommen, da du geschäftlich mit der TRO GmbH in Verbindung stehst. Deine eingegebenen Daten werden ausschließlich intern von TRO genutzt, um eine Übersicht über die Anzahl der Teilnehmenden zu erhalten.',
    'privacy_close_aria': 'Schließen',
  },
  en: {
    'loading': 'Loading…',
    'not_found_title': 'Event not found.',
    'not_found_body': 'Please double-check the link from your invitation.',
    'error_title': 'Something went wrong.',
    'error_body': 'Please reload the page.',
    'page_404_title': 'No event here.',
    'page_404_body': 'This page is only accessible via a direct event link.',

    'eyebrow_register': 'Registration',
    'eyebrow_closed': 'Registration closed',
    'eyebrow_done': 'Registered',

    'closed_deadline_text': 'Registration for this event has ended.',
    'closed_deadline_when': 'Closed at: {when}',
    'closed_manual': 'Registration is currently closed.',

    'field_first_name': 'First name',
    'field_last_name': 'Last name',
    'field_email': 'Email address',
    'field_extra_guests': 'Additional guests',

    'rsvp_question_solo': 'I will attend…',
    'rsvp_question_group': 'We will attend…',
    'rsvp_confirmed': 'Definitely',
    'rsvp_tentative': 'Maybe',

    'summary_alone': 'You are registering on your own.',
    'summary_with_one': 'You are registering + 1 additional person (2 people in total).',
    'summary_with_many': 'You are registering + {n} additional people ({total} in total).',

    'submit_idle': 'Register',
    'submit_busy': 'Sending…',

    'done_title': 'Thank you, {firstName}!',
    'done_body_one': "We have you registered for {event}. We're looking forward to seeing you on {date} at {time}.",
    'done_body_many': "We've registered {persons} people for {event}. We're looking forward to seeing you on {date} at {time}.",
    'done_tentative_note': 'You selected "Maybe" — feel free to let us know once you have a definite answer.',

    'err_email_taken': 'This email address is already registered for this event.',
    'err_invalid_email': 'Please enter a valid email address.',
    'err_invalid_first_name': 'Please enter a first name.',
    'err_invalid_last_name': 'Please enter a last name.',
    'err_invalid_extras': 'Number of additional guests must be between 0 and 10.',
    'err_invalid_rsvp': 'Please choose "Definitely" or "Maybe".',
    'err_closed': 'Registration is now closed.',
    'err_not_found': 'Event not found.',
    'err_generic': 'Registration failed. Please try again.',
    'err_guests_not_allowed': 'Additional guests are not allowed for this event.',

    'lang_toggle_aria': 'Switch language',

    'calendar_add_title': 'Add to calendar',
    'calendar_google': 'Google',
    'calendar_apple': 'Apple',
    'calendar_outlook': 'Outlook',

    'location_open_maps_aria': 'Open address in Google Maps',
    'more_info_link': 'Learn more',

    'privacy_link': 'Privacy notice',
    'privacy_title': 'Privacy notice',
    'privacy_body': 'You received this link because you are in a business relationship with TRO GmbH. The information you submit will only be used internally by TRO to keep track of the number of attendees.',
    'privacy_close_aria': 'Close',
  },
} as const;

export type StringKey = keyof typeof strings.de;

type Ctx = {
  lang: Lang;
  setLang: (l: Lang) => void;
  t: (k: StringKey, vars?: Record<string, string | number>) => string;
};

const LangContext = createContext<Ctx>({
  lang: 'de',
  setLang: () => undefined,
  t: (k) => k,
});

const STORAGE_KEY = 'tro-events-lang';

function detectInitial(): Lang {
  if (typeof window === 'undefined') return 'de';
  const saved = window.localStorage.getItem(STORAGE_KEY);
  if (saved === 'de' || saved === 'en') return saved;
  // Browser-Sprache als Fallback (everything-but-de → en).
  const nav = navigator.language?.toLowerCase() ?? '';
  return nav.startsWith('de') ? 'de' : 'en';
}

export function LangProvider({ children }: { children: ReactNode }) {
  const [lang, setLangState] = useState<Lang>(detectInitial);

  useEffect(() => {
    document.documentElement.lang = lang;
  }, [lang]);

  const setLang = (next: Lang) => {
    setLangState(next);
    try {
      window.localStorage.setItem(STORAGE_KEY, next);
    } catch {
      /* ignore — z.B. Safari Private Mode wirft. */
    }
  };

  const value = useMemo<Ctx>(() => ({
    lang,
    setLang,
    t: (k, vars) => interpolate(strings[lang][k] ?? strings.de[k] ?? k, vars),
  }), [lang]);

  return <LangContext.Provider value={value}>{children}</LangContext.Provider>;
}

export function useLang() {
  return useContext(LangContext);
}

function interpolate(s: string, vars?: Record<string, string | number>): string {
  if (!vars) return s;
  let out = s;
  for (const [key, value] of Object.entries(vars)) {
    out = out.split(`{${key}}`).join(String(value));
  }
  return out;
}

// Locale für Intl.DateTimeFormat-Calls. Behält dasselbe TZ-Behavior unabhängig
// von der Sprache (Datumswerte sind ISO/local, nicht TZ-konvertiert).
export function localeFor(lang: Lang): string {
  return lang === 'en' ? 'en-GB' : 'de-DE';
}
